import { callOpenRouter, getMaxOutputTokens } from './ai-api';
import { pickModelForTask } from './model-selector';
import { getConfigValue, getPrompt } from '../config/pipeline-registry';
import {
    CONTACT_EXTRACTION_PROMPT,
    ASSESSOR_EXTRACTION_PROMPT,
    RFQ_SUMMARY_PROMPT,
} from '../config/prompts';
import { buildTenantBlock } from './tenant-resolver';
import { devLog } from '../utils/dev-log';
import { finaliseContactExtraction } from './rfq-requesters';
import { loadRequesterTenantContext } from './rfq-requester-tenant';
import { aiDocumentFormat, aiDocumentLabel, fillAiDocumentLabel } from '../utils/ai-document-label';
import { resolveDocumentClassification } from '../utils/document-classification';

function assertContactExtractionActive(signal?: AbortSignal | null): void {
    if (signal?.aborted) throw new DOMException('Contact extraction cancelled', 'AbortError');
}

interface ContactExtractionArgs {
    content: string;
    /** The real file name: kept for the requester's provenance, never put in the prompt (CN5). */
    fileName: string;
    /**
     * What the prompt calls the document. Defaults to its format ("PDF",
     * "email"). Callers that combine several documents pass "3 documents".
     */
    documentLabel?: string;
    isAssessorNotes?: boolean;
    apiKey: string;
    model: string;
    setExtractingDetails: (val: boolean) => void;
    setExtractionMessage: (msg: string) => void;
    signal?: AbortSignal | null;
    projectId?: string | null;
    throwOnError?: boolean;
}

/**
 * Stage 1's contact extraction. For a quote request (not inspector notes) the
 * AI result is finished by finaliseContactExtraction: an email not printed in
 * the document, or on the builder's own domain, is dropped, and every
 * requester is listed with the labelled-field parser first and the AI second.
 * The parser reads the document text only, so a requester found there is
 * returned even when the AI call failed.
 */
export async function extractContactDetailsApi(args: ContactExtractionArgs): Promise<any> {
    const extracted = await requestContactDetails(args);
    if (args.isAssessorNotes || !args.content || args.content.length < 30) return extracted;
    const tenant = await loadRequesterTenantContext();
    assertContactExtractionActive(args.signal);
    return finaliseContactExtraction(extracted, {
        ...tenant,
        sourceText: args.content,
        doc: args.fileName,
    });
}

/**
 * What a Stage 1 extraction prompt calls its document (CN5, Peter 2026-10-13:
 * "file names shouldnt dictate anything. only the contents."): the caller's
 * neutral label, else the format, else "document". The file name itself
 * never reaches the model.
 */
export function stage1PromptDocumentLabel(fileName: unknown, documentLabel?: unknown): string {
    const explicit = typeof documentLabel === 'string' ? documentLabel.trim() : '';
    if (explicit) return explicit;
    return aiDocumentFormat(typeof fileName === 'string' ? fileName : '') || 'document';
}

/**
 * Several imported documents joined into one text for contact extraction and
 * the RFQ Summary, each headed by its position and format (CN5). The same text
 * fills the RFQ box, which later stages read, so no file name is written in it.
 */
export function buildStage1CombinedExtractionText(parts: Array<{ content?: string; name?: string; isEmail?: boolean }>): string {
    return (Array.isArray(parts) ? parts : [])
        .map((part, index) => `=== ${aiDocumentLabel(index + 1, part)} ===\n${part?.content || ''}`)
        .join('\n\n');
}

async function requestContactDetails({
    content,
    fileName,
    documentLabel,
    isAssessorNotes = false,
    apiKey,
    model,
    setExtractingDetails,
    setExtractionMessage,
    signal = null,
    projectId = null,
    throwOnError = false,
}: ContactExtractionArgs): Promise<any> {
    assertContactExtractionActive(signal);
    if (!apiKey || !content || content.length < 30) return null;

    setExtractingDetails(true);
    setExtractionMessage(isAssessorNotes ? 'Extracting inspector details from notes...' : 'Extracting contact details from RFQ...');

    try {
        const escapeDocTag = (s: string) => String(s || '').replace(/<\/document\s*>/gi, '<!-- /document --/>');
        const label = escapeDocTag(stage1PromptDocumentLabel(fileName, documentLabel));
        let extractionPrompt: string;

        if (isAssessorNotes) {
            const assessorTemplate = getPrompt('assessor-extraction') || ASSESSOR_EXTRACTION_PROMPT;
            extractionPrompt = fillAiDocumentLabel(assessorTemplate, label)
                .replace('${content}', escapeDocTag(content.substring(0, 8000)));
        } else {
            const contactTemplate = getPrompt('contact-extraction') || CONTACT_EXTRACTION_PROMPT;
            let tenantBlock = '(no instance-owner profile configured)';
            try {
                tenantBlock = await buildTenantBlock();
            } catch (tenantErr: any) {
                console.warn('[Stage1] buildTenantBlock failed, extraction will run without tenant context:', tenantErr?.message || tenantErr);
            }
            extractionPrompt = fillAiDocumentLabel(contactTemplate.replace('${TENANT_BLOCK}', tenantBlock), label)
                .replace('${content}', escapeDocTag(content));
        }

        let text: string;
        try {
            assertContactExtractionActive(signal);
            text = await (callOpenRouter as any)({
                apiKey,
                model: pickModelForTask(isAssessorNotes ? 'assessor-extraction' : 'contact-extraction', model),
                messages: [{ role: 'user', content: extractionPrompt }],
                temperature: getConfigValue('contact-extraction', 'temperature') ?? 0.1,
                maxTokens: getMaxOutputTokens(pickModelForTask(isAssessorNotes ? 'assessor-extraction' : 'contact-extraction', model)),
                pipelineId: isAssessorNotes ? 'assessor-extraction' : 'contact-extraction',
                stageName: '1',
                projectId,
                signal,
            });
            assertContactExtractionActive(signal);
        } catch (apiErr: any) {
            assertContactExtractionActive(signal);
            if (throwOnError || apiErr?.name === 'AbortError') throw apiErr;
            console.error(' API Error:', apiErr);
            setExtractionMessage(`API Error: ${apiErr?.message || 'Unknown error'}`);
            setTimeout(() => setExtractionMessage(''), 5000);
            return null;
        }

        if (!text || text.trim() === '') {
            console.warn('Empty response from API');
            setExtractionMessage('No response from AI. Please try again.');
            setTimeout(() => setExtractionMessage(''), 5000);
            return null;
        }

        console.log('[Stage1] Extraction response received (length:', text.length, ')');
        devLog('[Stage1] Extraction response body:', text.substring(0, 500));

        let jsonText = text.trim();
        const codeBlockMatch = jsonText.match(/```(?:json)?\s*(\{[\s\S]*?\})\s*```/i);
        if (codeBlockMatch && codeBlockMatch[1]) {
            jsonText = codeBlockMatch[1];
        } else {
            const jsonMatch = jsonText.match(/\{[\s\S]*\}/);
            if (jsonMatch) {
                jsonText = jsonMatch[0];
            }
        }

        if (jsonText) {
            try {
                const extracted = JSON.parse(jsonText);
                devLog('[Stage1] Extracted data:', extracted);

                const expectedFields = isAssessorNotes
                    ? ['assessorName', 'visitDate']
                    : ['clientName', 'clientCompany', 'clientEmail', 'clientPhone', 'address', 'strataPlan', 'orderNumber', 'siteContactName', 'siteContactPhone', 'visitDate', 'projectDescription'];
                const hasRecognizedField = expectedFields.some(f => extracted[f] !== null && extracted[f] !== undefined && extracted[f] !== '');
                if (!hasRecognizedField) {
                    console.warn('Extracted JSON has no recognized fields. Keys found:', Object.keys(extracted));
                    setExtractionMessage('No details found in document');
                    setTimeout(() => setExtractionMessage(''), 3000);
                    return null;
                }

                for (const key of Object.keys(extracted)) {
                    if (extracted[key] === 'null' || extracted[key] === 'N/A' || extracted[key] === 'n/a' || extracted[key] === 'Not provided' || extracted[key] === 'Not found') {
                        extracted[key] = null;
                    }
                }

                return extracted;
            } catch (parseErr) {
                console.error(' JSON Parse Error:', parseErr, 'Raw JSON text:', jsonText.substring(0, 500));

                try {
                    let repairedJson = jsonText.trim();
                    const openBraces = (repairedJson.match(/\{/g) || []).length;
                    const closeBraces = (repairedJson.match(/\}/g) || []).length;

                    if (openBraces > closeBraces) {
                        console.log(' Attempting to repair truncated JSON...');
                        const lastCompleteMatch = repairedJson.match(/.*("[w]+"[\s]*:[\s]*(?:"[^"]*"|null|true|false|\d+))[\s]*,?[\s]*$/s);

                        if (lastCompleteMatch && lastCompleteMatch[1]) {
                            const lastIndex = repairedJson.lastIndexOf(lastCompleteMatch[1]);
                            if (lastIndex !== -1) {
                                repairedJson = repairedJson.substring(0, lastIndex + lastCompleteMatch[1].length);
                            }
                        } else {
                            repairedJson = repairedJson.replace(/,\s*"[\w]*"?\s*:?\s*"?[^"]*$/s, '');
                            repairedJson = repairedJson.replace(/,\s*"[\w]*"?\s*$/s, '');
                        }

                        repairedJson = repairedJson.replace(/,\s*$/, '');
                        const newOpenBraces = (repairedJson.match(/\{/g) || []).length;
                        const newCloseBraces = (repairedJson.match(/\}/g) || []).length;
                        const bracesToAdd = newOpenBraces - newCloseBraces;

                        for (let i = 0; i < bracesToAdd; i++) {
                            repairedJson += '\n}';
                        }

                        console.log(' Repaired JSON:', repairedJson.substring(0, 300) + '...');
                        const extracted = JSON.parse(repairedJson);
                        devLog('[Stage1] Extracted data (repaired JSON):', extracted);
                        const hasData = Object.values(extracted).some(v => v !== null && v !== '');
                        if (hasData) {
                            setExtractionMessage('Partially extracted - some fields may be missing');
                            setTimeout(() => setExtractionMessage(''), 3000);
                            return extracted;
                        }
                    }
                } catch (repairErr) {
                    console.error(' JSON repair failed:', repairErr);
                }

                try {
                    let braceCount = 0;
                    let startIdx = -1;
                    for (let i = 0; i < text.length; i++) {
                        if (text[i] === '{') {
                            if (startIdx === -1) startIdx = i;
                            braceCount++;
                        } else if (text[i] === '}') {
                            braceCount--;
                            if (braceCount === 0 && startIdx !== -1) {
                                const jsonStr = text.substring(startIdx, i + 1);
                                const extracted = JSON.parse(jsonStr);
                                devLog('[Stage1] Extracted data (fallback parse):', extracted);
                                const hasData = Object.values(extracted).some(v => v !== null && v !== '');
                                if (hasData) {
                                    return extracted;
                                }
                                break;
                            }
                        }
                    }
                } catch (fallbackErr) {
                    console.error(' Fallback parse also failed:', fallbackErr);
                }
                setExtractionMessage('Failed to parse AI response - invalid JSON format');
                setTimeout(() => setExtractionMessage(''), 5000);
                return null;
            }
        }

        console.warn('No JSON found in response. Full text:', text.substring(0, 500));
        setExtractionMessage('AI response did not contain expected JSON data');
        setTimeout(() => setExtractionMessage(''), 5000);
        return null;
    } catch (error: any) {
        assertContactExtractionActive(signal);
        if (throwOnError || error?.name === 'AbortError') throw error;
        if (error?.name === 'AbortError') {
            console.error(' Request timed out');
            setExtractionMessage('Request timed out - please try again');
            setTimeout(() => setExtractionMessage(''), 5000);
        } else {
            console.error(' Contact extraction error:', error);
            setExtractionMessage('Extraction failed: ' + (error?.message || 'Unknown error'));
            setTimeout(() => setExtractionMessage(''), 5000);
        }
        return null;
    } finally {
        setExtractingDetails(false);
    }
}

/**
 * The RFQ Summary's list of the documents attached to the project (CN5): each
 * by its position, its format and the class its content was given, never its
 * file name. A class that came only from a file name is left out. Records of
 * one file are listed once, as before. CN6: two records are one file when
 * they carry the same content fingerprint or the same text, or failing both
 * the same id. The name is not read, so two different files that share a name
 * are both listed and a document with no name is listed too.
 */
function rfqInventoryIdentity(doc: any, position: number): string {
    const fingerprint = [doc.sha256, doc.contentFingerprint, doc.sourceFingerprint, doc.checksumSha256]
        .find(value => typeof value === 'string' && value.trim());
    if (fingerprint) return `fingerprint:${String(fingerprint).trim().toLowerCase()}`;
    const text = [doc.content, doc.text, doc.textContent]
        .find(value => typeof value === 'string' && value.trim());
    if (text) return `text:${String(text).replace(/\s+/g, ' ').trim()}`;
    const id = typeof doc.id === 'string' || typeof doc.id === 'number' ? String(doc.id).trim() : '';
    if (id) return `id:${id}`;
    return `position:${position}`;
}

export function buildRfqReferenceInventory(projectDocuments: unknown): string {
    const seen = new Set<string>();
    const unique = (Array.isArray(projectDocuments) ? projectDocuments : [])
        .filter((doc: any, position: number) => {
            if (!doc || typeof doc !== 'object') return false;
            const key = rfqInventoryIdentity(doc, position);
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
        });
    if (unique.length === 0) return '(no companion documents verified in this import batch)';
    return unique.map((doc: any, index: number) => {
        const resolved = resolveDocumentClassification(doc);
        const classFromContent = resolved.basis === 'trusted-canonical-class' || resolved.basis === 'direct-evidence';
        return `- ${aiDocumentLabel(index + 1, doc, classFromContent ? { classification: resolved.classification } : {})}`;
    }).join('\n');
}

export async function analyzeRfqSummaryApi({
    content,
    fileName,
    documentLabel,
    projectDocuments = [],
    apiKey,
    model,
}: {
    content: string;
    /** The real file name. Never put in the prompt (CN5). */
    fileName: string;
    documentLabel?: string;
    projectDocuments?: any[];
    apiKey: string;
    model: string;
}): Promise<string | null> {
    if (!apiKey || !content || content.length < 50) return null;
    try {
        const escapeDocTag = (s: string) => String(s || '').replace(/<\/document\s*>/gi, '<!-- /document --/>');
        const rfqTemplate = getPrompt('rfq-summary') || RFQ_SUMMARY_PROMPT;
        const referenceInventory = buildRfqReferenceInventory(projectDocuments);
        const rfqPrompt = fillAiDocumentLabel(
            rfqTemplate.replace('${referenceInventory}', () => referenceInventory),
            escapeDocTag(stage1PromptDocumentLabel(fileName, documentLabel)),
        )
            .replace('${content}', escapeDocTag(content));
        const response = await (callOpenRouter as any)({
            apiKey,
            model: pickModelForTask('rfq-summary', model),
            messages: [{ role: 'user', content: rfqPrompt }],
            temperature: getConfigValue('rfq-analysis', 'temperature') ?? 0.2,
            maxTokens: getMaxOutputTokens(pickModelForTask('rfq-summary', model)),
            pipelineId: 'rfq-summary',
            task: 'rfq-summary',
            stageName: '1',
        });
        return response;
    } catch (err) {
        console.error('RFQ analysis failed:', err);
        return null;
    }
}
